import { DiffMode, ReferenceDirection, ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import {
  BubbleChartGridMode,
  BubbleChartViewSettings,
  FieldSource,
} from '@ardoq/bubble-chart';
import { CommonDropdownOptions, LayoutOrientation } from '@ardoq/global-consts';
import {
  DEFAULT_TRAVERSE_REFERENCE_TYPES,
  DEFAULT_TRAVERSE_SETTINGS,
  DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
} from './consts';
import { settingsBarConsts } from '@ardoq/settings-bar';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { MAX_LIMIT_VALUES, SizeBasedOn } from 'views/treemapConsts';
import { CONTEXT_NODE_RADIUS } from '@ardoq/graph';
import { AxisSortOrder } from 'tabview/componentMatrix/componentMatrix2/types';
import {
  DEFAULT_CONTOUR_VIEW_SETTINGS,
  DEFAULT_NETWORK_3D_VIEW_SETTINGS,
  DEFAULT_POINTS_3D_VIEW_SETTINGS,
  DEFAULT_POLAR_VIEW_SETTINGS,
  DEFAULT_SPIDER_VIEW_SETTINGS,
  DEFAULT_SURFACE_3D_VIEW_SETTINGS,
} from '@ardoq/xyz-charts';

/**
 * These settings are needed to maintain the view state across sessions or on page reload.
 * These settings are the only settings we should load and save in respective modes.
 *
 * The needs may vary between different environments.
 *
 * Keys MUST stay enumerable on the defaults object even when their value is
 * `undefined`: `sanitizeSingleViewSettings` strips any key from incoming
 * updates that isn't a key of the defaults, so omitting a key would silently
 * drop persisted updates for it.
 */

export const DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE = {
  [ViewIds.RELATIONSHIPS_3]: {
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    bundleRelationships: false,
  },
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: {
    layoutOrientation: LayoutOrientation.LEFT_TO_RIGHT,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    separateReferences: false,
    componentStyle: 'icon' as const,
  },
  [ViewIds.BLOCKS]: {
    layoutData: {},
    linksAggregation: 'separate' as const,
    componentStyle: 'icon' as const,
    arrangements: [],
    pendingArrangementContainerIds: [],
  },
  [ViewIds.TIMELINE]: {
    selectedMilestoneFields: [],
    selectedPhaseFields: [WILDCARD],
    domainRange: [null, null] satisfies [null, null],
    gridPrecision: '*' as const,
    persistedCollapsedState: {
      collapsedGroupIds: [],
      collapsedFieldDataIds: [],
    },
  },
  [ViewIds.BUBBLE]: {
    selectedFieldNameX: '',
    selectedFieldNameY: '',
    selectedFieldNameRadius: '',
    background: BubbleChartGridMode.NONE,
    customQuadrantSettings: {
      xMinimum: NaN,
      xMaximum: NaN,
      yMinimum: NaN,
      yMaximum: NaN,
      labels: ['one', 'two', 'three', 'four'],
    },
    bubbleAreaScaleFactor: 0.5,
    fieldSource: FieldSource.BY_COMPONENT,
    includeAllDescendants: false,
    selectedReferenceTypeName: null,
    selectedReferenceDirection: ReferenceDirection.INCOMING,
    valueSourceComponentTypes: null,
    showBubblesWithZeroValue: true,
    selectedComponentTypes: [WILDCARD],
    selectedQuadrant: null,
    labelFilter: undefined,
  } satisfies Omit<BubbleChartViewSettings, 'isLegendActive'>,
  [ViewIds.DEPENDENCY_MAP_2]: {
    showVertical: false,
    colorByReference: false,
    showOnlyConnectedComponents: false,
    treeDepth: Infinity,
    degreesOfParenthood: 0,
  },
  [ViewIds.COMPONENT_MATRIX_2]: {
    selectedFieldNamesX: [],
    selectedFieldNamesY: [],
    referenceTypeX: null,
    referenceTypeY: null,
    showEmptyValues: false,
    isLegendActive: false,
    // ARD-31884: component-type narrowing. Empty array ⇒ "All", which
    // matches the existing behavior for viewpoints saved before the
    // selector was added.
    selectedComponentTypes: [],
    persistedExpandedState: {
      expandedRowAddresses: [],
      expandedColumnAddresses: [],
    },
    userAdjustedColumnWidths: {},
    userAdjustedRowHeights: {},
    // Row-headers-area width (ARD-32764): default `undefined` so the area
    // auto-fits content until the user drags the handle, then the resolved
    // width is persisted.
    userAdjustedRowHeadersWidth: undefined,
    sortOrderX: AxisSortOrder.DEFAULT,
    sortOrderY: AxisSortOrder.DEFAULT,
    displayTextInViewX: null,
    displayTextInViewY: null,
    useParentAsX: false,
    useParentAsY: false,
    additionalHeaderFieldNamesX: [],
    additionalHeaderFieldNamesY: [],
    colorRowColumnHeaderByComponent: true,
  },
  [ViewIds.ASSISTANT_DIAGRAM]: {
    isLegendActive: false,
  },
  [ViewIds.ASSISTANT_BLOCKS]: {
    isLegendActive: false,
    arrangements: [],
    title: null,
    collapsedGroupIds: [],
    separateReferences: false,
    hideReferences: false,
  },
  /* `maxDepth: 0` draws every level; `sizeByFieldName: null` sizes by component count, which is
     what `toSizing` falls back to. */
  [ViewIds.HIERARCHICAL_TREEMAP]: {
    sizeByFieldName: null,
    tilingMethod: 'squarify' as const,
    maxDepth: 0,
  },
  /* No `tilingMethod`: these two draw circles and rings and have no tiling to choose. On the
     shared settings type they persisted one anyway and nothing read it. */
  [ViewIds.HIERARCHICAL_PACKING]: {
    sizeByFieldName: null,
    maxDepth: 0,
  },
  [ViewIds.SUNBURST]: {
    sizeByFieldName: null,
    maxDepth: 0,
  },
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: {
    isLegendActive: false,
    // Block Diagram settings — must keep Block Diagram's field types so the
    // flag-off downgrade reads them with no conversion (ARD-33247).
    layoutOrientation: LayoutOrientation.LEFT_TO_RIGHT,
    separateReferences: false,
    componentStyle: 'icon' as const,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    // Flexible Block Diagram additions:
    layoutType: 'process' as const,
    hideReferences: false,
  },
  [ViewIds.SURFACE_3D]: DEFAULT_SURFACE_3D_VIEW_SETTINGS,
  [ViewIds.CONTOUR]: DEFAULT_CONTOUR_VIEW_SETTINGS,
  [ViewIds.SCATTER_3D]: DEFAULT_POINTS_3D_VIEW_SETTINGS,
  [ViewIds.BUBBLE_3D]: DEFAULT_POINTS_3D_VIEW_SETTINGS,
  [ViewIds.NETWORK_3D]: {
    ...DEFAULT_NETWORK_3D_VIEW_SETTINGS,
    /* The degree, which is the measure a graph is usually about and the only one available
       without a field. */
    nodeSizeBy: 'references' as const,
    sizeByFieldName: '',
    isLegendActive: false,
    /* Open. A grouping a reader asked for is a grouping they want to see the contents of, and
       every other grouping view opens the same way. */
    collapsedGroupIds: [],
  },
  [ViewIds.SPIDER_2]: DEFAULT_SPIDER_VIEW_SETTINGS,
  [ViewIds.POLAR]: DEFAULT_POLAR_VIEW_SETTINGS,
};

export const DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE = {
  [ViewIds.PAGESVIEW]: {
    ...DEFAULT_TRAVERSE_REFERENCE_TYPES,
    includeFields: [CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT],
    hideEmptyFields: false,
    expandDescription: false,
    includeIncomingReferenceTypes: [CommonDropdownOptions.ALL],
    includeOutgoingReferenceTypes: [CommonDropdownOptions.ALL],
  },
  [ViewIds.TAGSCAPE]: {
    excludeUnused: false,
    count: false,
    excludeComps: false,
    excludeRefs: false,
  },
  [ViewIds.TABLEVIEW]: {
    visibleColumns: [],
    expandDescription: false,
    hideAllColumns: false,
    [viewSettingsConsts.IS_LEGEND_ACTIVE]: false,
  },
  [ViewIds.COMPONENTTREE]: {
    [viewSettingsConsts.IS_LEGEND_ACTIVE]: false,
  },
  [ViewIds.DEPWHEEL]: {},
  [ViewIds.TREEMAP]: {
    sizeBasedOn: SizeBasedOn.CHILD_COUNT,
    maxLimitValue: MAX_LIMIT_VALUES[MAX_LIMIT_VALUES.length - 1],
    [settingsBarConsts.IS_LEGEND_ACTIVE]: false, // new view
  },
  [ViewIds.BLOCK_DIAGRAM]: {
    layoutOrientation: LayoutOrientation.LEFT_TO_RIGHT,
    ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    separateReferences: false,
    useNewGrouping: true,
  },
  [ViewIds.SWIMLANE]: {
    incomingDegreesOfRelationship: 0,
    outgoingDegreesOfRelationship: 99,
    orderAlphabetically: false,
    isVertical: false,
  },
  [ViewIds.DEPMATRIX]: {
    referenceCreationType: {},
    referenceToWsId: null,
    showOnlyConnectedComponents: false,
  },
  [ViewIds.ROADMAP]: { selectedFieldName: undefined },
  [ViewIds.DEPENDENCY_MAP_2]: {
    showVertical: false,
    colorByReference: false,
    ...DEFAULT_TRAVERSE_SETTINGS,
    includeOutgoingReferenceTypeNames: [
      CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD,
    ],
    includeIncomingReferenceTypeNames: [
      CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD,
    ],
    showOnlyConnectedComponents: false,
    treeDepth: Infinity,
    degreesOfParenthood: 0,
    degreesOfChildhood: 0,
    seenTraversalInfo: false,
    useNewGrouping: true,
  },
  [ViewIds.SPIDER]: {
    aggregateChildValues: 'sum' as const,
    /** @type Record<string, boolean> */
    selectedFieldNames: {},
    sideways: false,
    [viewSettingsConsts.IS_LEGEND_ACTIVE]: false,
    includeAllDescendants: false,
  },
  [ViewIds.REFERENCETABLE]: {
    visibleColumns: [],
    expandDescription: false,
    hideAllColumns: false,
  },
  [ViewIds.CAPABILITY_MAP]: {
    selectedFieldName: '',
    supportingValue: '',
    groupingTypeId: '',
    isLayoutDirectionAnyVertical: true,
    includeIncomingReferenceTypes: [],
    includeOutgoingReferenceTypes: [],
    treeDepth: Infinity,
  },
  [ViewIds.BUBBLE]: {
    selectedFieldNameX: '',
    selectedFieldNameY: '',
    selectedFieldNameRadius: '',
    background: BubbleChartGridMode.NONE,
    isLegendActive: false,
    customQuadrantSettings: {
      xMinimum: NaN,
      xMaximum: NaN,
      yMinimum: NaN,
      yMaximum: NaN,
      labels: ['one', 'two', 'three', 'four'],
    },
    bubbleAreaScaleFactor: 0.5,
    fieldSource: FieldSource.BY_COMPONENT,
    includeAllDescendants: false,
    selectedReferenceTypeName: null,
    selectedReferenceDirection: ReferenceDirection.INCOMING,
    valueSourceComponentTypes: null,
    showBubblesWithZeroValue: true,
    selectedComponentTypes: [WILDCARD],
    selectedQuadrant: null,
  } satisfies BubbleChartViewSettings,
  [ViewIds.TIMELINE]: {
    ...DEFAULT_TRAVERSE_SETTINGS,
    isLegendActive: false,
    selectedMilestoneFields: [],
    selectedPhaseFields: [WILDCARD],
    useNewGrouping: true,
    domainRange: [null, null] satisfies [null, null],
    gridPrecision: '*' as const,
    persistedCollapsedState: {
      collapsedGroupIds: [],
      collapsedFieldDataIds: [],
    },
  },
  [ViewIds.COMPONENT_MATRIX]: {
    selectedFieldNamesX: [],
    selectedFieldNamesY: [],
    referenceTypeX: null,
    referenceTypeY: null,
    filterNulls: false,
    includeAllDescendants: false,
  },
  [ViewIds.NETWORK]: {
    ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    nodeDistance: CONTEXT_NODE_RADIUS,
    animate: true,
  },
  [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: {
    layoutOrientation: LayoutOrientation.LEFT_TO_RIGHT,
    ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    selectedFieldNameValue: '',
    separateReferences: false,
  },
  [ViewIds.RELATIONSHIPS_3]: {
    ...DEFAULT_WORKSPACE_MODE_TRAVERSE_SETTINGS,
    activeDiffMode: DiffMode.SCENARIO,
    collapsedGroupIds: [],
    bundleRelationships: true,
    highlightDisconnectedComponents: false,
  },
  // #region discontinued views
  [ViewIds.CIRCULAR_RELATIONSHIP_DIAGRAM]: {},
  [ViewIds.SEQUENCE]: {},
  [ViewIds.REFERENCE_AGGREGATION_VIEW]: {},
  [ViewIds.PROCESSFLOW]: {},
  [ViewIds.HEATMAP]: {},
  [ViewIds.API_DOCUMENTATION]: {},
  [ViewIds.RELATIONSHIPS]: {},
  [ViewIds.DEPMAP]: {},
  // #endregion
};
